import React, { useCallback, useEffect, useState } from 'react';
import {
  AdminReviewApiError,
  fetchMotionClaimReview,
  normalizeReviewerId,
  submitMotionClaimApproval,
  type MotionClaimApprovalAction,
  type MotionClaimReviewClaim,
  type MotionClaimReviewResponse,
} from '../../lib/adminReviewApi';

export interface MotionClaimReviewPanelViewProps {
  uploadId: string;
  payload: MotionClaimReviewResponse | null;
  loading: boolean;
  error: string | null;
  reviewerId: string;
  onReviewerIdChange: (value: string) => void;
  actionLoadingWindowId?: string | null;
  cardNotes: Record<string, string>;
  cardSuccessMessages?: Record<string, string>;
  onCardNoteChange: (windowId: string, value: string) => void;
  onAction: (
    windowId: string,
    action: MotionClaimApprovalAction,
    approvedWording?: string
  ) => void;
}

const InternalReviewBadge: React.FC = () => (
  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
    Internal review only
  </span>
);

function gateTierLabel(status: string): string {
  if (status === 'customer_safe') return 'Customer safe';
  if (status === 'coach_review_only') return 'Coach review';
  return status;
}

function gateTierClass(status: string): string {
  if (status === 'customer_safe') {
    return 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200';
  }
  return 'bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-200';
}

function promotionStatusLabel(status?: string): string {
  switch (status) {
    case 'eligible_for_admin_review':
      return 'Eligible for admin review';
    case 'needs_human_confirmation':
      return 'Needs human confirmation';
    case 'internal_only':
      return 'Internal only';
    case 'blocked':
      return 'Blocked';
    default:
      return status || 'Unknown';
  }
}

function promotionStatusClass(status?: string): string {
  switch (status) {
    case 'eligible_for_admin_review':
      return 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200';
    case 'needs_human_confirmation':
      return 'bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-200';
    default:
      return 'bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-200';
  }
}

function approvalStatusLabel(status?: string): string {
  switch (status) {
    case 'approved_for_future_projection':
      return 'Approved for future projection';
    case 'rejected_for_customer_projection':
      return 'Rejected for customer projection';
    case 'needs_more_review':
      return 'Needs more review';
    case 'internal_only':
      return 'Internal only';
    default:
      return status || '';
  }
}

function approvalStatusClass(status?: string): string {
  switch (status) {
    case 'approved_for_future_projection':
      return 'bg-violet-100 text-violet-900 dark:bg-violet-900/40 dark:text-violet-200';
    case 'rejected_for_customer_projection':
      return 'bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-200';
    case 'needs_more_review':
      return 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200';
    default:
      return 'bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-200';
  }
}

function formatConcept(concept?: string | null, display?: string | null): string {
  if (display) return display;
  if (!concept) return 'Unknown concept';
  return concept.replace(/_/g, ' ');
}

const ELIGIBILITY_WARNING_LABELS: Record<string, string> = {
  banned_wording_in_source: 'Source wording contains banned or outcome-adjacent wording',
  play_type_alias_applied: 'Play type was normalized from an alias',
  unknown_concept: 'Concept is not in approved taxonomy',
  unknown_concept_family: 'Concept is not in approved taxonomy',
  low_evidence: 'Evidence level is too low',
  unknown_play_type: 'Play type is not in approved taxonomy',
  unknown_attack_area: 'Attack area is not in approved taxonomy',
  future_promotion_ineligible: 'Claim is not eligible for future projection',
};

export function formatEligibilityWarningLabel(warning: string): string {
  return ELIGIBILITY_WARNING_LABELS[warning] || warning;
}

export function formatEligibilityBlockReason(claim: MotionClaimReviewClaim): string {
  const warnings = claim.promotion_contract?.normalization_warnings || [];
  if (warnings.length > 0) {
    return warnings.map(formatEligibilityWarningLabel).join(', ');
  }
  if (claim.promotion_contract?.future_promotion_eligible === false) {
    return formatEligibilityWarningLabel('future_promotion_ineligible');
  }
  return 'Not eligible for future projection approval';
}

export function formatActionTargetLabel(claim: MotionClaimReviewClaim): string {
  return formatConcept(
    claim.concept_family,
    claim.promotion_contract?.normalized.concept_display
  );
}

export function isApproveEligible(claim: MotionClaimReviewClaim): boolean {
  return (
    claim.claim_gate_status === 'customer_safe' &&
    claim.promotion_contract?.promotion_status === 'eligible_for_admin_review' &&
    claim.promotion_contract?.future_promotion_eligible === true
  );
}

export const MotionClaimReviewPanelView: React.FC<MotionClaimReviewPanelViewProps> = ({
  uploadId,
  payload,
  loading,
  error,
  reviewerId,
  onReviewerIdChange,
  actionLoadingWindowId,
  cardNotes,
  cardSuccessMessages = {},
  onCardNoteChange,
  onAction,
}) => {
  if (loading) {
    return (
      <section className="rounded-lg border border-secondary-200 p-4 text-sm text-secondary-600 dark:border-secondary-700 dark:text-secondary-300">
        Loading motion claim review…
      </section>
    );
  }

  if (error) {
    return (
      <section className="rounded-lg border border-secondary-200 p-4 text-sm text-secondary-600 dark:border-secondary-700 dark:text-secondary-300">
        Motion claim review unavailable: {error}
      </section>
    );
  }

  if (!payload) {
    return null;
  }

  const customerSafeClaims = payload.claims.filter(
    (claim) => claim.claim_gate_status === 'customer_safe'
  );
  const coachReviewClaims = payload.claims.filter(
    (claim) => claim.claim_gate_status === 'coach_review_only'
  );
  const reviewerReady = normalizeReviewerId(reviewerId).length > 0;

  const renderClaimCard = (claim: MotionClaimReviewClaim) => {
    const approveEligible = isApproveEligible(claim);
    const cardBusy = actionLoadingWindowId === claim.window_id;
    const note = cardNotes[claim.window_id] || '';

    return (
      <article
        key={claim.window_id}
        className="rounded-lg border border-secondary-200 p-3 dark:border-secondary-700"
        data-testid={`motion-claim-card-${claim.window_id}`}
        data-gate-status={claim.claim_gate_status}
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs text-secondary-500 dark:text-secondary-400">
            {claim.window_id}
          </span>
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${gateTierClass(
              claim.claim_gate_status
            )}`}
          >
            {gateTierLabel(claim.claim_gate_status)}
          </span>
          {claim.promotion_contract?.promotion_status ? (
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${promotionStatusClass(
                claim.promotion_contract.promotion_status
              )}`}
              data-testid={`promotion-status-${claim.window_id}`}
            >
              {promotionStatusLabel(claim.promotion_contract.promotion_status)}
            </span>
          ) : null}
          {claim.approval?.approval_status ? (
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${approvalStatusClass(
                claim.approval.approval_status
              )}`}
              data-testid={`approval-status-${claim.window_id}`}
            >
              {approvalStatusLabel(claim.approval.approval_status)}
            </span>
          ) : null}
          <InternalReviewBadge />
        </div>
        <p className="mt-2 text-sm text-secondary-900 dark:text-white">{claim.review_wording}</p>
        <dl className="mt-3 grid grid-cols-2 gap-2 text-xs text-secondary-600 dark:text-secondary-300">
          <div>
            <dt className="font-medium text-secondary-500 dark:text-secondary-400">Play type</dt>
            <dd>{claim.play_type}</dd>
          </div>
          <div>
            <dt className="font-medium text-secondary-500 dark:text-secondary-400">Concept</dt>
            <dd>
              {formatConcept(
                claim.concept_family,
                claim.promotion_contract?.normalized.concept_display
              )}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-secondary-500 dark:text-secondary-400">Confidence</dt>
            <dd>{claim.confidence_label}</dd>
          </div>
          <div>
            <dt className="font-medium text-secondary-500 dark:text-secondary-400">Evidence</dt>
            <dd>{claim.evidence_label}</dd>
          </div>
          {claim.attack_area ? (
            <div className="col-span-2">
              <dt className="font-medium text-secondary-500 dark:text-secondary-400">Attack area</dt>
              <dd>
                {claim.promotion_contract?.normalized.attack_area_display ||
                  claim.attack_area.replace(/_/g, ' ')}
              </dd>
            </div>
          ) : null}
          {claim.promotion_contract?.normalization_warnings?.length ? (
            <div className="col-span-2">
              <dt className="font-medium text-secondary-500 dark:text-secondary-400">Normalization</dt>
              <dd>{claim.promotion_contract.normalization_warnings.join(', ')}</dd>
            </div>
          ) : null}
        </dl>

        <div className="mt-4 space-y-2 border-t border-secondary-200 pt-3 dark:border-secondary-700">
          <div
            className="text-xs text-secondary-600 dark:text-secondary-300"
            data-testid={`action-context-${claim.window_id}`}
          >
            <p>
              <span className="font-medium text-secondary-500 dark:text-secondary-400">
                Selected window:
              </span>{' '}
              <span className="font-mono">{claim.window_id}</span>
            </p>
            <p>
              <span className="font-medium text-secondary-500 dark:text-secondary-400">
                Action target:
              </span>{' '}
              {formatActionTargetLabel(claim)}
            </p>
          </div>
          {claim.promotion_contract?.future_promotion_eligible !== true ? (
            <p
              className="rounded bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-900/20 dark:text-amber-200"
              data-testid={`eligibility-block-${claim.window_id}`}
            >
              Future projection approval is blocked for this claim.
              <br />
              Reason: {formatEligibilityBlockReason(claim)}
            </p>
          ) : null}
          {cardSuccessMessages[claim.window_id] ? (
            <p
              className="rounded bg-emerald-50 p-2 text-xs text-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-200"
              data-testid={`action-success-${claim.window_id}`}
            >
              {cardSuccessMessages[claim.window_id]}
            </p>
          ) : null}
          <label className="block text-xs font-medium text-secondary-500 dark:text-secondary-400">
            Reviewer note
            <textarea
              className="mt-1 w-full rounded border border-secondary-300 bg-white p-2 text-xs dark:border-secondary-600 dark:bg-secondary-900"
              rows={2}
              value={note}
              onChange={(event) => onCardNoteChange(claim.window_id, event.target.value)}
              data-testid={`reviewer-note-${claim.window_id}`}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-primary text-xs disabled:opacity-50"
              disabled={!reviewerReady || !approveEligible || cardBusy}
              data-testid={`approve-${claim.window_id}`}
              onClick={() =>
                onAction(
                  claim.window_id,
                  'approve_for_future_projection',
                  claim.review_wording
                )
              }
            >
              Approve for future projection
            </button>
            <button
              type="button"
              className="btn-secondary text-xs disabled:opacity-50"
              disabled={!reviewerReady || cardBusy}
              data-testid={`reject-${claim.window_id}`}
              onClick={() => onAction(claim.window_id, 'reject_for_customer_projection')}
            >
              Reject
            </button>
            <button
              type="button"
              className="btn-secondary text-xs disabled:opacity-50"
              disabled={!reviewerReady || cardBusy}
              data-testid={`needs-review-${claim.window_id}`}
              onClick={() => onAction(claim.window_id, 'needs_more_review')}
            >
              Needs more review
            </button>
            <button
              type="button"
              className="btn-secondary text-xs disabled:opacity-50"
              disabled={!reviewerReady || cardBusy}
              data-testid={`internal-only-${claim.window_id}`}
              onClick={() => onAction(claim.window_id, 'mark_internal_only')}
            >
              Internal only
            </button>
          </div>
        </div>
      </article>
    );
  };

  return (
    <section
      className="space-y-4 rounded-lg border border-secondary-200 p-4 dark:border-secondary-700"
      data-testid="motion-claim-review-panel"
      data-upload-id={uploadId}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">
          Motion Claim Review
        </h3>
        <InternalReviewBadge />
      </div>
      <p className="text-xs text-secondary-500 dark:text-secondary-400">
        Internal shadow review for gated Gemini motion claims. Approval only marks this claim as
        eligible for future projection. It does not publish to customers.
      </p>

      <label className="block text-xs font-medium text-secondary-700 dark:text-secondary-200">
        Reviewer ID
        <input
          className="mt-1 w-full rounded border border-secondary-300 bg-white p-2 text-sm dark:border-secondary-600 dark:bg-secondary-900"
          value={reviewerId}
          onChange={(event) => onReviewerIdChange(event.target.value)}
          placeholder="admin:antoine"
          data-testid="motion-claim-reviewer-id"
        />
      </label>

      <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
        <div className="rounded bg-secondary-50 p-2 dark:bg-secondary-800/60">
          <div className="font-medium text-secondary-500 dark:text-secondary-400">Customer safe</div>
          <div className="text-lg font-semibold text-secondary-900 dark:text-white">
            {payload.summary.customer_safe_count}
          </div>
        </div>
        <div className="rounded bg-secondary-50 p-2 dark:bg-secondary-800/60">
          <div className="font-medium text-secondary-500 dark:text-secondary-400">Coach review</div>
          <div className="text-lg font-semibold text-secondary-900 dark:text-white">
            {payload.summary.coach_review_only_count}
          </div>
        </div>
        <div className="rounded bg-secondary-50 p-2 dark:bg-secondary-800/60">
          <div className="font-medium text-secondary-500 dark:text-secondary-400">Shadow only</div>
          <div className="text-lg font-semibold text-secondary-900 dark:text-white">
            {payload.summary.shadow_only_count}
          </div>
        </div>
        <div className="rounded bg-secondary-50 p-2 dark:bg-secondary-800/60">
          <div className="font-medium text-secondary-500 dark:text-secondary-400">Blocked</div>
          <div className="text-lg font-semibold text-secondary-900 dark:text-white">
            {payload.summary.blocked_count}
          </div>
        </div>
      </div>

      {payload.claims.length === 0 ? (
        <p className="text-sm text-secondary-600 dark:text-secondary-300">
          No customer-safe or coach-review motion claims available for this upload.
        </p>
      ) : (
        <div className="space-y-4">
          {customerSafeClaims.length > 0 ? (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-secondary-500 dark:text-secondary-400">
                Customer safe
              </h4>
              <div className="space-y-2">{customerSafeClaims.map(renderClaimCard)}</div>
            </div>
          ) : null}
          {coachReviewClaims.length > 0 ? (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-secondary-500 dark:text-secondary-400">
                Coach review
              </h4>
              <div className="space-y-2">{coachReviewClaims.map(renderClaimCard)}</div>
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
};

interface MotionClaimReviewPanelProps {
  uploadId: string | null | undefined;
}

const MotionClaimReviewPanel: React.FC<MotionClaimReviewPanelProps> = ({ uploadId }) => {
  const [payload, setPayload] = useState<MotionClaimReviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reviewerId, setReviewerId] = useState('');
  const [cardNotes, setCardNotes] = useState<Record<string, string>>({});
  const [cardSuccessMessages, setCardSuccessMessages] = useState<Record<string, string>>({});
  const [actionLoadingWindowId, setActionLoadingWindowId] = useState<string | null>(null);

  const loadReview = useCallback(async () => {
    if (!uploadId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetchMotionClaimReview(uploadId);
      setPayload(response);
    } catch (err: unknown) {
      if (err instanceof AdminReviewApiError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to load motion claim review');
      }
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [uploadId]);

  useEffect(() => {
    if (!uploadId) {
      setPayload(null);
      setError(null);
      return;
    }
    loadReview();
  }, [uploadId, loadReview]);

  const handleAction = useCallback(
    async (
      windowId: string,
      action: MotionClaimApprovalAction,
      approvedWording?: string
    ) => {
      if (!uploadId || !normalizeReviewerId(reviewerId)) return;
      setActionLoadingWindowId(windowId);
      setError(null);
      try {
        const result = await submitMotionClaimApproval(uploadId, windowId, {
          action,
          reviewer_id: reviewerId,
          reviewer_note: cardNotes[windowId] || undefined,
          approved_wording: approvedWording,
        });
        setCardSuccessMessages((current) => ({
          ...current,
          [windowId]: `Updated ${windowId}: ${approvalStatusLabel(result.approval.approval_status)}`,
        }));
        await loadReview();
      } catch (err: unknown) {
        if (err instanceof AdminReviewApiError) {
          setError(err.message);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Failed to submit motion claim approval');
        }
      } finally {
        setActionLoadingWindowId(null);
      }
    },
    [uploadId, reviewerId, cardNotes, loadReview]
  );

  if (!uploadId) {
    return null;
  }

  return (
    <MotionClaimReviewPanelView
      uploadId={uploadId}
      payload={payload}
      loading={loading}
      error={error}
      reviewerId={reviewerId}
      onReviewerIdChange={setReviewerId}
      actionLoadingWindowId={actionLoadingWindowId}
      cardNotes={cardNotes}
      cardSuccessMessages={cardSuccessMessages}
      onCardNoteChange={(windowId, value) =>
        setCardNotes((current) => ({ ...current, [windowId]: value }))
      }
      onAction={handleAction}
    />
  );
};

export default MotionClaimReviewPanel;
